export function filterRecords(records, source, filters = {}) {
    const fields = source === 'hc'
        ? [['hc_type', filters.hcType], ['status', filters.status], ['country', filters.country]]
        : [['sub_category', filters.subCategory], ['name', filters.vendorName]];
    return (records || []).filter(r => fields.every(([key, value]) => (
        Array.isArray(value) ? (!value.length || value.includes(r[key])) : (!value || r[key] === value)
    )));
}

export function monthInScope(month, { quarter = [], customRange = null, period = [], asOf = null, yearMode = 'current', priorYear = '' } = {}) {
    const displayMonth = yearMode === 'prior' ? `${priorYear}-${month.slice(5)}` : month;
    if (customRange && (displayMonth < customRange.start || displayMonth > customRange.end)) return false;
    // Quarter/Period are multi-select (array of selected values) — a month
    // is in scope if it matches ANY selected quarter, and an empty selection
    // (same as the old '' / default 'fy') means no restriction at all.
    const quarters = Array.isArray(quarter) ? quarter : (quarter ? [quarter] : []);
    if (quarters.length && !quarters.some(q => Math.ceil(Number(month.slice(5, 7)) / 3) === Number(q.slice(1)))) return false;
    const periods = Array.isArray(period) ? period : (period ? [period] : []);
    // Full Year (or no selection) includes every scoped month; Year to Date
    // alone restricts to months up to asOf. Selecting both is a no-op toward
    // Full Year, since YTD's months are always a subset of FY's.
    if (periods.includes('ytd') && !periods.includes('fy')) return !!asOf && month <= asOf;
    return true;
}

// Fiscal Year is multi-select too, but blending "Current" + "Prior" means
// summing two structurally different data lenses (prior year substitutes
// its own actual into the "actual" slot, with no AOP) rather than just
// loosening an equality check — so scopeSummaryRow/scopedMetricValue below
// run the normal single-year-mode logic once per selected mode and sum the
// results, instead of threading array-awareness through monthInScope.
function normalizeYearModes(yearMode) {
    const modes = Array.isArray(yearMode) ? yearMode : (yearMode ? [yearMode] : []);
    return modes.length ? modes : ['current'];
}

export function recordMetrics(record, months, currentYear, priorYear) {
    const values = record.monthly_values || {};
    const sum = (block, year) => months.reduce((n, month) => n + Number(values[block]?.[`${year}-${month.slice(5)}`] || 0), 0);
    // In a prior-year view there is no AOP or earlier comparison available.
    if (!priorYear) return { actual: sum(`actual_${currentYear}`, currentYear), aop: 0, py: 0 };
    return {
        actual: sum(`actual_fcst_${currentYear}`, currentYear),
        aop: sum(`aop_${currentYear}`, currentYear),
        py: sum(`actual_${priorYear}`, priorYear),
    };
}

export function sumMetrics(rows) {
    return rows.reduce((sum, row) => ({ actual: sum.actual + row.actual, aop: sum.aop + row.aop, py: sum.py + row.py }), { actual: 0, aop: 0, py: 0 });
}

export function scopeSummaryRow(row, options) {
    // Build one filtered+remapped monthly series per selected Fiscal Year
    // mode, then merge them by month (summing actual/aop/py) — so Current +
    // Prior selected together show one blended series instead of just
    // whichever mode happened to run first. The common case (one mode
    // selected) merges a single series with itself, a no-op.
    const perMode = normalizeYearModes(options.yearMode).map(ym => (row?.monthly || [])
        .filter(m => monthInScope(m.month, { ...options, yearMode: ym }))
        .map(m => ym === 'prior' ? { ...m, actual: m.py, aop: 0, py: 0 } : m));
    const byMonth = new Map();
    perMode.flat().forEach(m => {
        const existing = byMonth.get(m.month);
        byMonth.set(m.month, existing
            ? { ...existing, actual: existing.actual + m.actual, aop: existing.aop + m.aop, py: existing.py + m.py }
            : { ...m });
    });
    const monthly = Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
    const totals = sumMetrics(monthly);
    const ytd = sumMetrics(monthly.filter(m => options.asOf && m.month <= options.asOf));
    const quarterly = Object.fromEntries([1, 2, 3, 4].map(q => [`Q${q}`, sumMetrics(monthly.filter(m => Math.ceil(Number(m.month.slice(5, 7)) / 3) === q))]));
    return {
        ...row, monthly, quarterly,
        fy_actual_forecast: totals.actual, fy_aop: totals.aop, fy_prior_year: totals.py,
        ytd_actual: ytd.actual, ytd_aop: ytd.aop, ytd_prior_year: ytd.py,
        act_vs_aop_ytd: ytd.actual - ytd.aop, act_vs_py_ytd: ytd.actual - ytd.py,
        fcst_vs_aop_full_year: totals.actual - totals.aop, fcst_vs_py_full_year: totals.actual - totals.py,
    };
}

// Every figure on this dashboard is expressed in thousands with a trailing
// "K" — no switching to M/B at larger magnitudes and no un-abbreviated
// numbers either, so every table/card uses one consistent unit instead of
// a reader having to mentally convert between $19M and $609.2K on the same
// screen.
export function money(value) {
    const num = Number(value || 0);
    const sign = num < 0 ? '-' : '';
    const thousands = Math.round(Math.abs(num) / 1000);
    return `${sign}$${thousands.toLocaleString('en-US')}K`;
}

export function variancePct(actual, base) {
    return base ? `${((actual - base) / Math.abs(base) * 100).toFixed(1)}%` : 'N/A (zero base)';
}

// Scopes a revenue_arr metric ({monthly: [{month,label,actual,aop,py}],
// is_balance}) the same way scopeSummaryRow scopes a regular row — but
// collapses the scoped months differently depending on what kind of figure
// it is: Revenue is a flow (summed across the months in scope, same as
// every G&A dollar figure), while Ending ARR is a point-in-time balance (the
// LAST month in scope, not a sum of 12 month-end snapshots — summing a
// balance across months would wildly overstate it).
export function scopedMetricValue(metric, field, options) {
    if (!metric) return null;
    // Same per-mode-then-sum approach as scopeSummaryRow, for the same
    // reason — see its comment above.
    return normalizeYearModes(options.yearMode).reduce((sum, ym) => {
        const inScope = (metric.monthly || []).filter(m => monthInScope(m.month, { ...options, yearMode: ym }));
        if (!inScope.length) return sum;
        const pick = row => (ym === 'prior' ? row.py : row[field]) || 0;
        return sum + (metric.is_balance ? pick(inScope[inScope.length - 1]) : inScope.reduce((s, row) => s + pick(row), 0));
    }, 0);
}

// G&A spend as a % of a Revenue/ARR denominator — null (not 0) when the
// denominator is missing/zero, so callers can show "Unavailable" instead of
// a misleading 0% or Infinity.
export function pctOfMetric(gaTotalValue, metricValue) {
    if (gaTotalValue == null || metricValue == null || !metricValue) return null;
    return (gaTotalValue / metricValue) * 100;
}

// Renders a {revenue, arr} percentage pair (from pctOfMetric) as one
// compact line — null when both are unavailable, so callers can skip
// rendering the sub-line entirely instead of showing an empty one. JSX in a
// .js (not .jsx) file is fine here — this whole CS_Elements folder
// already does it (e.g. CSGASummary.js's VarianceStack).
export function pctLine(pct) {
    if (!pct || (pct.revenue == null && pct.arr == null)) return null;
    return <>{pct.revenue != null && `${pct.revenue.toFixed(1)}% of Revenue`}{pct.revenue != null && pct.arr != null && ' · '}{pct.arr != null && `${pct.arr.toFixed(1)}% of ARR`}</>;
}

// Scopes a revenue_arr metric by an explicit list of months (rather than a
// period/quarter/customRange description) — used by drill-down modals that
// already know exactly which months they're showing. Same balance-vs-flow
// collapsing rule as scopedMetricValue.
export function scopedMetricValueForMonths(metric, field, months) {
    if (!metric) return null;
    const monthsSet = new Set(months);
    const inScope = (metric.monthly || []).filter(m => monthsSet.has(m.month)).sort((a, b) => a.month.localeCompare(b.month));
    if (!inScope.length) return 0;
    return metric.is_balance ? (inScope[inScope.length - 1][field] || 0) : inScope.reduce((sum, m) => sum + (m[field] || 0), 0);
}
